import React from 'react';
import styled from 'styled-components';

const Stack = styled.div`display: grid; gap: 1rem;`;
const Label = styled.label`color: #334155; display: grid; font-size: 0.9rem; font-weight: 800; gap: 0.4rem;`;
const Helper = styled.span`color: #64748b; font-size: 0.8rem; font-weight: 500;`;
const Input = styled.input`
  background: #fff; border: 1px solid ${(p) => (p.$error ? '#dc2626' : '#cbd5e1')};
  border-radius: 0.65rem; font: inherit; min-height: 46px; padding: 0.7rem 0.8rem; width: 100%;
`;
const Textarea = styled.textarea`
  background: #fff; border: 1px solid ${(p) => (p.$error ? '#dc2626' : '#cbd5e1')};
  border-radius: 0.65rem; font: inherit; min-height: 110px; padding: 0.8rem; resize: vertical; width: 100%;
`;
const ChoiceGrid = styled.div`display: flex; flex-wrap: wrap; gap: 0.55rem;`;
const Chip = styled.button`
  background: ${(p) => (p.$active ? '#1b4332' : '#fff')};
  border: 1px solid #819088; border-radius: 999px;
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  cursor: pointer; font-weight: 750; padding: 0.5rem 0.85rem;
`;
const Error = styled.p`color: #b91c1c; font-size: 0.8rem; margin: 0;`;

export default function DynamicOfferingFields({
  fields = [],
  values = {},
  errors = {},
  onChange,
}) {
  if (!fields.length) return null;

  const setValue = (key, value) => onChange?.({ ...values, [key]: value });

  return (
    <Stack>
      {fields.map((field) => {
        const value = values[field.key];
        const error = errors[field.key];
        const label = (
          <>
            {field.label}
            {field.required ? ' *' : ''}
            {field.help_text ? <Helper>{field.help_text}</Helper> : null}
          </>
        );

        if (field.type === 'textarea') {
          return (
            <Label key={field.key}>
              {label}
              <Textarea
                $error={Boolean(error)}
                value={value || ''}
                onChange={(event) => setValue(field.key, event.target.value)}
              />
              {error ? <Error>{error}</Error> : null}
            </Label>
          );
        }

        if (field.type === 'number') {
          return (
            <Label key={field.key}>
              {label}
              <Input
                type="number"
                $error={Boolean(error)}
                value={value ?? ''}
                onChange={(event) => setValue(field.key, event.target.value)}
              />
              {error ? <Error>{error}</Error> : null}
            </Label>
          );
        }

        if (field.type === 'yes_no') {
          return (
            <div key={field.key}>
              <Label as="div">{label}</Label>
              <ChoiceGrid>
                {['Yes', 'No'].map((option) => (
                  <Chip
                    key={option}
                    type="button"
                    $active={value === option}
                    onClick={() => setValue(field.key, option)}
                  >
                    {option}
                  </Chip>
                ))}
              </ChoiceGrid>
              {error ? <Error>{error}</Error> : null}
            </div>
          );
        }

        if (field.type === 'single_choice') {
          return (
            <div key={field.key}>
              <Label as="div">{label}</Label>
              <ChoiceGrid>
                {(field.options || []).map((option) => (
                  <Chip
                    key={option}
                    type="button"
                    $active={value === option}
                    onClick={() => setValue(field.key, option)}
                  >
                    {option}
                  </Chip>
                ))}
              </ChoiceGrid>
              {error ? <Error>{error}</Error> : null}
            </div>
          );
        }

        if (field.type === 'multi_choice') {
          const selected = Array.isArray(value) ? value : [];
          return (
            <div key={field.key}>
              <Label as="div">{label}</Label>
              <ChoiceGrid>
                {(field.options || []).map((option) => {
                  const active = selected.includes(option);
                  return (
                    <Chip
                      key={option}
                      type="button"
                      $active={active}
                      onClick={() => {
                        setValue(
                          field.key,
                          active
                            ? selected.filter((item) => item !== option)
                            : [...selected, option],
                        );
                      }}
                    >
                      {option}
                    </Chip>
                  );
                })}
              </ChoiceGrid>
              {error ? <Error>{error}</Error> : null}
            </div>
          );
        }

        return (
          <Label key={field.key}>
            {label}
            <Textarea
              $error={Boolean(error)}
              value={value || ''}
              onChange={(event) => setValue(field.key, event.target.value)}
              placeholder="Type your answer"
            />
            {error ? <Error>{error}</Error> : null}
          </Label>
        );
      })}
    </Stack>
  );
}
